import type { AppNotification } from './types';

/**
 * Where a notification leads. ``null`` = nowhere to go (the object is gone or its access was
 * taken away): the item is only marked read. The target is re-authorized by the server when it
 * opens, so a stale link is a graceful "not found", never a leak.
 */
export function notificationTarget(n: AppNotification): string | null {
  switch (n.type) {
    case 'invitation.workspace':
    case 'invitation.board':
    case 'invitation.database':
      return '/app/invitations';
    case 'invitation.accepted':
    case 'invitation.declined':
      if (typeof n.payload['database_id'] === 'string') {
        return `/app/databases/${n.payload['database_id']}`;
      }
      return n.board_id
        ? `/app/boards/${n.board_id}`
        : n.workspace_id
          ? `/app/workspaces/${n.workspace_id}?tab=invitations`
          : null;
    case 'request.join':
      return n.board_id
        ? `/app/boards/${n.board_id}?access=requests`
        : n.workspace_id
          ? `/app/workspaces/${n.workspace_id}?tab=requests`
          : null;
    case 'transfer.requested':
      return n.workspace_id ? `/app/workspaces/${n.workspace_id}?tab=requests` : null;
    case 'request.approved':
      return n.board_id
        ? `/app/boards/${n.board_id}`
        : n.workspace_id
          ? `/app/workspaces/${n.workspace_id}`
          : '/app';
    case 'request.rejected':
    case 'transfer.approved':
    case 'transfer.rejected':
      return '/app';
    case 'task.assigned':
    case 'comment.mention':
    case 'comment.reply':
    case 'task.updated':
      return n.board_id && n.card_id ? `/app/boards/${n.board_id}/cards/${n.card_id}` : null;
    case 'feedback.submitted':
    case 'feedback.author_reply':
    case 'feedback.assigned':
    case 'feedback.reassigned':
    case 'feedback.reply':
    case 'feedback.status':
      // The server re-checks access when the page opens: a revoked moderator just gets "not found".
      return n.entity_id ? `/app/feedback/${n.entity_id}` : '/app/feedback';
    case 'member.role_changed':
      return n.workspace_id ? `/app/workspaces/${n.workspace_id}` : null;
    case 'board.role_changed':
      return n.board_id ? `/app/boards/${n.board_id}` : null;
    case 'board.owner_assigned':
      return n.board_id ? `/app/boards/${n.board_id}` : null;
    case 'member.removed':
    case 'board.access_removed':
    case 'board.access_revoked':
    case 'workspace.deleted':
      return null;
    default:
      // A policy notification about an administrative event: the board (when the viewer may
      // see it), else the workspace's audit log, else nowhere.
      if (n.type.startsWith('audit.')) {
        if (n.board_id) return `/app/boards/${n.board_id}`;
        return n.workspace_id ? `/app/workspaces/${n.workspace_id}?tab=audit` : null;
      }
      return null;
  }
}
